import { useMemo } from 'react';
import { Box, Container, Stack } from '@mui/material';
import { Config } from 'final-form';
import { Form } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { formatPhoneNumberNationalForUS } from '@ocx/utils-phone-number';
import { Button, Typography, LoadingButton, Markdown, Note } from '@ocx/ui';
import { InfoIcon, HeartIcon } from '@ocx/ui-icons';
import { DateField, TextField, CheckboxField, localization, SwitchField, FormIconHelperText } from '@ocx/ui-forms';
import { buttonTextMessages } from '@ocx-app/modules/localization/button-text.messages';

import { ProfileFormFields, ProfileFormFieldsInitialValues } from './edit-profile.types';
import { messages } from './edit-profile.messages';

export type EditProfileProps = {
  loading: boolean;
  isProfileCompleted: boolean;
  initialValues: ProfileFormFieldsInitialValues;
  updateCustomer: Config<ProfileFormFields>['onSubmit'];
  onCloseClick(): void;
  validationSchema: Config<ProfileFormFields>['validate'];
  maxPossibleYOB: number;
  minPossibleYOB: number;
  termsConditionsLink: string;
  privacyPolicyLink: string;
  isExplicitTermsAcceptanceEnabled: boolean;
  isExplicitSmsPromotionAcceptanceEnabled: boolean;
};

export const EditProfile = ({
  loading,
  initialValues,
  updateCustomer,
  isProfileCompleted,
  onCloseClick,
  validationSchema,
  minPossibleYOB,
  maxPossibleYOB,
  termsConditionsLink,
  privacyPolicyLink,
  isExplicitTermsAcceptanceEnabled,
  isExplicitSmsPromotionAcceptanceEnabled,
}: EditProfileProps) => {
  const intl = useIntl();
  const isExplicitSmsPromotionAcceptanceVisible = !isProfileCompleted && isExplicitSmsPromotionAcceptanceEnabled;
  const [disclaimerMarkdown, explicitMarketingSmsAcceptanceMarkdown] = useMemo(
    () => [
      intl.formatMessage(messages.disclaimerMarkdown, {
        termsConditionsLink,
        privacyPolicyLink,
      }),
      intl.formatMessage(messages.explicitMarketingSmsAcceptanceToggleLabel, {
        termsConditionsLink,
        privacyPolicyLink,
      }),
    ],
    [intl, privacyPolicyLink, termsConditionsLink],
  );

  const birthdateFieldDescription = useMemo(() => intl.formatMessage(messages.birthdateFieldDescription), [intl]);

  // TODO: refactor, remove Container
  return (
    <Container
      maxWidth="md"
      sx={{
        p: 0,
      }}>
      <Typography variant="h5">
        <FormattedMessage {...(isProfileCompleted ? messages.completedProfile : messages.notCompletedProfile)} />
      </Typography>
      <Box alignItems="center" marginTop={3}>
        <Form<ProfileFormFields>
          validate={validationSchema}
          onSubmit={updateCustomer}
          // @ts-expect-error initialValues
          initialValues={initialValues}
          render={(props) => (
            <form onSubmit={props.handleSubmit}>
              <Stack spacing={2}>
                <TextField
                  variant="outlined"
                  name="firstName"
                  fullWidth
                  label={intl.formatMessage(localization.formLabelMessages['form-label:first-name'])}
                  disabled={loading}
                />
                <TextField
                  variant="outlined"
                  name="lastName"
                  fullWidth
                  label={intl.formatMessage(localization.formLabelMessages['form-label:last-name'])}
                  disabled={loading}
                />
                <Stack>
                  <TextField
                    variant="outlined"
                    name="email"
                    fullWidth
                    type="email"
                    label={intl.formatMessage(localization.formLabelMessages['form-label:email'])}
                    disabled={loading}
                  />
                  <FormIconHelperText icon={InfoIcon} text={intl.formatMessage(messages.emailFieldDescription)} />
                </Stack>
                <TextField
                  variant="outlined"
                  name="phoneNumber"
                  fullWidth
                  disabled
                  label={intl.formatMessage(localization.formLabelMessages['form-label:phone-number'])}
                  fieldProps={{ format: formatPhoneNumberNationalForUS }}
                />
                {isExplicitSmsPromotionAcceptanceVisible && (
                  <Note display="flex" alignItems="center" justifyContent="space-between">
                    <Markdown variant="caption" color="textSecondary">
                      {explicitMarketingSmsAcceptanceMarkdown}
                    </Markdown>
                    <SwitchField
                      disabled={loading}
                      name="isExplicitMarketingSmsAcceptanceOptIn"
                      formControlLabelProps={{ sx: { marginRight: 0 } }}
                    />
                  </Note>
                )}
                <Stack>
                  <Typography variant="subtitle1" color="textSecondary" paragraph>
                    <FormattedMessage {...messages.birthdateFieldLabel} />
                  </Typography>
                  <DateField
                    minYear={minPossibleYOB}
                    maxYear={maxPossibleYOB}
                    fieldNamePrefix="birthdate"
                    disabled={loading}
                  />
                  {birthdateFieldDescription !== 'none' && (
                    <FormIconHelperText icon={HeartIcon} text={birthdateFieldDescription} />
                  )}
                </Stack>
                {disclaimerMarkdown !== 'none' && (
                  <Note>
                    <Markdown variant="caption" color="textSecondary">
                      {disclaimerMarkdown}
                    </Markdown>
                  </Note>
                )}
                {isExplicitTermsAcceptanceEnabled && (
                  <CheckboxField
                    name="termsAccepted"
                    label={
                      <Markdown variant="caption" color="textSecondary">
                        {intl.formatMessage(messages.termsAcceptanceCheckboxLabel, {
                          termsConditionsLink,
                          privacyPolicyLink,
                        })}
                      </Markdown>
                    }
                    disabled={loading}
                    formControlLabelProps={{
                      sx: { '& .MuiCheckbox-root': { alignSelf: 'flex-start' } },
                    }}
                  />
                )}
                <LoadingButton
                  loading={loading}
                  disabled={props.hasValidationErrors || props.submitting || loading || !props.dirty}
                  type="submit"
                  fullWidth>
                  <FormattedMessage {...buttonTextMessages['button-text:submit']} />
                </LoadingButton>
                <Button variant="text" onClick={onCloseClick}>
                  <FormattedMessage {...buttonTextMessages['button-text:close']} />
                </Button>
              </Stack>
            </form>
          )}
        />
      </Box>
    </Container>
  );
};
